import { Head, router } from "@inertiajs/react";
import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface RangeOption {
  key: string;
  label: string;
}

interface SummaryProps {
  totalEvents: number;
  uniqueVisits: number;
  conversions: number;
  conversionRate: number;
  averageTimeToConversion?: number | null;
  conversionEventBreakdown: Record<string, number>;
}

interface JourneyEvent {
  name: string;
  time: string | null;
  timeFromStart: number;
  conversion: boolean;
  properties: Record<string, unknown>;
  userId?: number | null;
}

interface RecentJourney {
  visitId: number;
  visitToken: string;
  user?: { id: number; email: string } | null;
  startedAt: string | null;
  lastEventAt: string | null;
  conversion: boolean;
  conversionEvent?: string | null;
  durationToConversion?: number | null;
  events: JourneyEvent[];
}

interface TopJourney {
  steps: string[];
  count: number;
  averageDurationSeconds: number;
  lastConversionAt: string | null;
}

interface AdminEventsPageProps {
  rangeKey: string;
  availableRanges: RangeOption[];
  summary: SummaryProps;
  topJourneys: TopJourney[];
  recentJourneys: RecentJourney[];
}

const numberFormatter = new Intl.NumberFormat();
const percentageFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatDuration(seconds?: number | null) {
  if (seconds == null) return "—";
  if (seconds < 0) return "—";
  if (seconds < 1) return "<1s";

  const units: [label: string, value: number][] = [
    ["d", 86_400],
    ["h", 3_600],
    ["m", 60],
    ["s", 1],
  ];

  let remaining = Math.floor(seconds);
  const parts: string[] = [];

  for (const [label, value] of units) {
    if (remaining >= value) {
      const amount = Math.floor(remaining / value);
      remaining -= amount * value;
      parts.push(`${amount}${label}`);
    }
    if (parts.length === 2) break;
  }

  if (parts.length === 0) {
    return "<1s";
  }

  return parts.join(" ");
}

function renderProperties(properties: Record<string, unknown>) {
  const entries = Object.entries(properties);
  if (entries.length === 0) {
    return <span className="text-xs text-slate-400 dark:text-slate-500">No metadata</span>;
  }

  return (
    <dl className="space-y-1">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-2">
          <dt className="w-28 text-xs font-medium tracking-wide text-slate-500 uppercase dark:text-slate-400">
            {key.replace(/_/g, " ")}
          </dt>
          <dd className="flex-1 text-sm text-slate-700 dark:text-slate-300">
            {Array.isArray(value) ? value.join(", ") : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function AdminEventsPage({
  rangeKey,
  availableRanges,
  summary,
  topJourneys,
  recentJourneys,
}: AdminEventsPageProps) {
  const [expandedJourneys, setExpandedJourneys] = useState<Set<string>>(new Set());

  const activeRangeLabel = useMemo(
    () => availableRanges.find((range) => range.key === rangeKey)?.label,
    [availableRanges, rangeKey],
  );

  const sortedBreakdown = useMemo(
    () =>
      Object.entries(summary.conversionEventBreakdown).sort(
        ([, countA], [, countB]) => Number(countB) - Number(countA),
      ),
    [summary.conversionEventBreakdown],
  );

  const handleRangeChange = (value: string) => {
    if (value === rangeKey) return;

    router.get("/admin/events", { range: value }, { preserveState: true, preserveScroll: true, replace: true });
  };

  const toggleJourney = (visitToken: string) => {
    setExpandedJourneys((prev) => {
      const next = new Set(prev);
      if (next.has(visitToken)) {
        next.delete(visitToken);
      } else {
        next.add(visitToken);
      }
      return next;
    });
  };

  return (
    <>
      <Head title="Admin · User journey insights" />
      <div className="container mx-auto max-w-7xl space-y-10 px-6 py-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-50">User journey insights</h1>
            <p className="mt-1 text-slate-600 dark:text-slate-400">
              Understand how people move through the product before they convert.
            </p>
          </div>

          <Select value={rangeKey} onValueChange={handleRangeChange}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {availableRanges.map((range) => (
                <SelectItem key={range.key} value={range.key}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Total events</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {numberFormatter.format(summary.totalEvents)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Unique visits</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {numberFormatter.format(summary.uniqueVisits)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Conversions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {numberFormatter.format(summary.conversions)}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Conversion rate {percentageFormatter.format(summary.conversionRate)}%
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">
                Avg. time to convert
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {formatDuration(summary.averageTimeToConversion)}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Top conversion journeys</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">{activeRangeLabel ?? "Selected range"}</p>
            </div>

            {topJourneys.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-slate-100 dark:border-slate-700">
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-900">
                    <TableRow>
                      <TableHead className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                        Steps
                      </TableHead>
                      <TableHead className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                        Conversions
                      </TableHead>
                      <TableHead className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                        Avg. time to convert
                      </TableHead>
                      <TableHead className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                        Last conversion
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-white dark:bg-slate-800">
                    {topJourneys.map((journey) => (
                      <TableRow
                        key={journey.steps.join("-")}
                        className="divide-y divide-slate-100 dark:divide-slate-700"
                      >
                        <TableCell className="px-4 py-4">
                          <div className="flex flex-wrap gap-2">
                            {journey.steps.map((step) => (
                              <span
                                key={step}
                                className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                              >
                                {step}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-4 font-semibold text-slate-900 dark:text-slate-100">
                          {numberFormatter.format(journey.count)}
                        </TableCell>
                        <TableCell className="px-4 py-4 text-slate-700 dark:text-slate-300">
                          {formatDuration(journey.averageDurationSeconds)}
                        </TableCell>
                        <TableCell className="px-4 py-4 text-slate-700 dark:text-slate-300">
                          {journey.lastConversionAt
                            ? dateTimeFormatter.format(new Date(journey.lastConversionAt))
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-slate-600 dark:text-slate-400">
                No conversion journeys recorded for this period.
              </p>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Conversion events</h2>
            {sortedBreakdown.length > 0 ? (
              <ul className="mt-4 space-y-3 text-sm">
                {sortedBreakdown.map(([name, count]) => (
                  <li
                    key={name}
                    className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
                  >
                    <span className="font-medium text-slate-700 dark:text-slate-300">{name}</span>
                    <span className="text-slate-600 dark:text-slate-400">{numberFormatter.format(Number(count))}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                No conversion events captured for this period.
              </p>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Recent journeys</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Most recent visits that interacted with tracking events.
            </p>
          </div>

          {recentJourneys.length > 0 ? (
            <div className="mt-6 space-y-6">
              {recentJourneys.map((journey) => {
                const isExpanded = expandedJourneys.has(journey.visitToken);

                return (
                  <div
                    key={journey.visitToken}
                    className="overflow-hidden rounded-lg border border-slate-100 bg-slate-50 dark:border-slate-700 dark:bg-slate-900"
                  >
                    <button
                      onClick={() => toggleJourney(journey.visitToken)}
                      className="w-full p-4 text-left transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-3">
                            <ChevronDown
                              className={cn(
                                "h-5 w-5 text-slate-500 transition-transform dark:text-slate-400",
                                isExpanded && "rotate-180",
                              )}
                            />
                            <div>
                              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                Visit ID
                                <span className="ml-1 font-semibold text-slate-900 dark:text-slate-100">
                                  {journey.visitId}
                                </span>
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                Visit token {journey.visitToken}
                              </p>
                            </div>
                          </div>
                          <div className="text-right text-sm text-slate-600 dark:text-slate-400">
                            <p>
                              Started{" "}
                              {journey.startedAt ? dateTimeFormatter.format(new Date(journey.startedAt)) : "Unknown"}
                            </p>
                            <p>
                              Last event{" "}
                              {journey.lastEventAt
                                ? dateTimeFormatter.format(new Date(journey.lastEventAt))
                                : "Unknown"}
                            </p>
                          </div>
                        </div>
                        <div className="ml-8 flex flex-wrap items-center gap-2">
                          {journey.user ? (
                            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300">
                              User {journey.user.email}
                            </span>
                          ) : (
                            <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                              Guest visitor
                            </span>
                          )}
                          {journey.conversion && (
                            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                              Converted
                            </span>
                          )}
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-slate-200 p-4 dark:border-slate-700">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          {journey.conversion ? (
                            <>
                              <span className="rounded-full bg-indigo-100 px-3 py-1 font-medium text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                                Converted via {journey.conversionEvent ?? "unknown event"}
                              </span>
                              {journey.durationToConversion != null && (
                                <span className="rounded-full bg-orange-100 px-3 py-1 font-medium text-orange-700 dark:bg-orange-900 dark:text-orange-300">
                                  Time to convert: {formatDuration(journey.durationToConversion)}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="rounded-full bg-slate-200 px-3 py-1 font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                              No conversion
                            </span>
                          )}
                        </div>

                        <div className="mt-4 overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
                          <Table>
                            <TableHeader className="bg-slate-100 dark:bg-slate-900">
                              <TableRow>
                                <TableHead className="px-4 py-2 text-left font-medium text-slate-600 dark:text-slate-300">
                                  Event
                                </TableHead>
                                <TableHead className="px-4 py-2 text-left font-medium text-slate-600 dark:text-slate-300">
                                  When
                                </TableHead>
                                <TableHead className="px-4 py-2 text-left font-medium text-slate-600 dark:text-slate-300">
                                  Offset
                                </TableHead>
                                <TableHead className="px-4 py-2 text-left font-medium text-slate-600 dark:text-slate-300">
                                  Details
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {journey.events.map((event, index) => (
                                <TableRow
                                  key={`${event.name}-${index}`}
                                  className={cn(
                                    "transition-colors",
                                    event.conversion
                                      ? "bg-emerald-50 dark:bg-emerald-950"
                                      : "bg-white dark:bg-slate-800",
                                  )}
                                >
                                  <TableCell className="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">
                                    {event.name}
                                  </TableCell>
                                  <TableCell className="px-4 py-3 text-slate-600 dark:text-slate-400">
                                    {event.time ? dateTimeFormatter.format(new Date(event.time)) : "Unknown"}
                                  </TableCell>
                                  <TableCell className="px-4 py-3 text-slate-600 dark:text-slate-400">
                                    {event.timeFromStart > 0 ? formatDuration(event.timeFromStart) : "Start"}
                                  </TableCell>
                                  <TableCell className="px-4 py-3 text-slate-600 dark:text-slate-400">
                                    {renderProperties(event.properties)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-6 text-sm text-slate-600 dark:text-slate-400">
              No recent journeys were recorded for this timeframe.
            </p>
          )}
        </div>
      </div>
    </>
  );
}
